import { useFetchUser } from '@/features/authorization/api/useFetchUser'
import { Block, Chart, Spinner, Text, TextTooltip } from '@/ui-kit'
import { useUiKit } from '@/ui-kit/providers'
import { formatDatetimeToLocalTime } from '@/utils'
import { formatNumber } from '@/utils/formatNumber'
import { Trans, useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import dayjs from 'dayjs'
import { Info } from 'lucide-react'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { ReactNode, useMemo } from 'react'
import { Period } from '../../types'
import { PeriodTabs } from '../PeriodTabs'
import { formatChartXAxisTitle } from './formatChart'

interface ChartBlockProps {
  data?: Record<Period, Record<string, string>>
  data2?: Record<Period, Record<string, string>>
  data3?: Record<Period, Record<string, string>>
  data4?: Record<Period, Record<string, string>>
  data5?: Record<Period, Record<string, string>>
  title: { singular: string; plural: string }
  noDataText?: ReactNode
  period: Period
  onPeriodChange: (value: Period) => void
  targetDate?: { title: string; date: string }
  isPercents?: boolean
  isStackedBars?: boolean
  className?: string
  tooltipNode?: ReactNode
}

export function ChartBlock({
  data,
  data2,
  data3,
  data4,
  data5,
  title,
  noDataText,
  period,
  onPeriodChange,
  targetDate,
  isPercents,
  isStackedBars,
  className,
  tooltipNode
}: ChartBlockProps): React.JSX.Element {
  const { data: user } = useFetchUser(true)
  const { t } = useLingui()
  const isStackedBarsLoading = data === undefined || data3 === undefined
  const { theme = { colorGray100to70: '' }, isMobile, isThemeDark } = useUiKit()

  function formatChartTooltipDescription(value: string): ReactNode {
    let title = value
    let info: string | undefined = undefined

    switch (period) {
      case 'day':
        title = formatDatetimeToLocalTime(value)

        if (targetDate && dayjs(value).isSame(targetDate.date, 'hour')) {
          info = targetDate.title
        }
        break
      case 'month':
        title = dayjs(value).format('D MMMM')

        if (targetDate && dayjs(value).isSame(targetDate.date, 'day')) {
          info = targetDate.title
        }
        break
      case 'year':
        title = dayjs(value).format('MMMM')

        if (targetDate && dayjs(value).isSame(targetDate.date, 'month')) {
          info = targetDate.title
        }
        break
    }

    return info ? (
      <>
        {title}
        <span className="color">{info}</span>
      </>
    ) : (
      title
    )
  }

  const items = useMemo(
    () =>
      data?.[period]
        ? Object.entries(data[period])
            .map(([key, value]) => {
              const totalValue = Number(value)

              const result: {
                title: string
                value: number
                value2?: number
                value3?: number
                value4?: number
                value5?: number
              } = {
                title: key,
                value: isPercents ? Number(totalValue) * 100 : Number(totalValue)
              }

              if (data2?.[period]) {
                result.value2 = data2[period][key]
                  ? isPercents
                    ? Number(data2[period][key]) * 100
                    : Number(data2[period][key])
                  : 0
              }
              if (data3?.[period]) {
                result.value3 = data3[period][key]
                  ? isPercents
                    ? Number(data3[period][key]) * 100
                    : Number(data3[period][key])
                  : 0
              }
              if (data4?.[period]) {
                result.value4 = data4[period][key]
                  ? isPercents
                    ? Number(data4[period][key]) * 100
                    : Number(data4[period][key])
                  : 0
              }

              if (data5?.[period]) {
                result.value5 = data5[period][key]
                  ? isPercents
                    ? Number(data5[period][key]) * 100
                    : Number(data5[period][key])
                  : 0
              }

              return result
            })
            .sort((a, b) => (dayjs(a.title).unix() < dayjs(b.title).unix() ? -1 : 1))
        : [],
    [data, data2, data3, data4, data5, isPercents, period]
  )

  const [total, total2, total3, total4, total5] = useMemo(() => {
    let validDatesCount = 0

    return items.reduce(
      (result, item, index) => {
        if (isPercents && targetDate) {
          switch (period) {
            case 'year':
              if (dayjs(targetDate.date).isAfter(item.title, 'month')) {
                return result
              }
              break
            case 'month':
              if (dayjs(targetDate.date).isAfter(item.title, 'day')) {
                return result
              }
              break
            case 'day':
              if (dayjs(targetDate.date).isAfter(item.title, 'hour')) {
                return result
              }
              break
          }
        }

        result[0] += item.value
        result[1] += item.value2 || 0
        result[2] += item.value3 || 0
        result[3] += item.value4 || 0
        result[4] += item.value5 || 0

        if (isPercents) {
          validDatesCount += 1

          if (index === items.length - 1) {
            result[0] /= validDatesCount
            result[1] /= validDatesCount
            result[2] /= validDatesCount
            result[3] /= validDatesCount
            result[4] /= validDatesCount
          }
        }

        return result
      },
      [0, 0, 0, 0, 0]
    )
  }, [isPercents, items, period, targetDate])

  return (
    <Block className={className}>
      <div className="max-mobile:flex-col max-mobile:items-stretch flex items-center justify-between">
        {isStackedBars ? (
          <div className="max-mobile:mb-[10px] flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              <TextTooltip
                text={
                  data4 !== undefined ? t`Data Points + Hive Points + Referral Points` : t`Data Points + Hive Points`
                }
                side="top"
              >
                <Text className="flex gap-2.5" type="subtitle-semibold">
                  <Trans>DataHive Points</Trans>:{' '}
                  <span>
                    {isStackedBarsLoading ? (
                      <Spinner size="small" />
                    ) : (
                      formatNumber(total + total2 + total3 + total4 + total5, 0)
                    )}
                  </span>
                </Text>
              </TextTooltip>
              {user?.stakingMultiplier &&
                user?.stakingMultiplier > 1 &&
                (() => {
                  const multiplier = formatNumber(user.stakingMultiplier, 2)
                  return (
                    <TextTooltip text={t`Points Multiplier applied based on your SOL stake`} side="top">
                      <div className="border-green/20 text-green text-body-3 flex items-center gap-1 rounded-lg border border-solid px-2 py-1">
                        <Trans>Points Multiplier {multiplier}x</Trans>
                        <Info className="h-3 w-3" />
                      </div>
                    </TextTooltip>
                  )
                })()}
            </div>
            <ul className={clsx('max-mobile:flex-col -ml-4 flex flex-wrap', isStackedBarsLoading && 'opacity-0')}>
              <li className="mx-4">
                <TextTooltip
                  className="flex items-center gap-2 self-start"
                  text={data5 !== undefined ? t`For collecting data and paid calls` : t`For collecting data`}
                >
                  <div className="backgroundColor h-3 w-3 rounded-[3px]" />
                  <Text className="whitespace-nowrap" type="body1-semibold">
                    <Trans>Data Points</Trans>: {formatNumber(total + total2 + total5, 0)}
                  </Text>
                </TextTooltip>
              </li>
              <li className="borderColorGray100to70 max-mobile:ml-0 max-mobile:border-0 max-mobile:pr-0 mr-4 -ml-[1px] border-l-[1px] border-solid pl-4">
                <TextTooltip className="flex items-center gap-2 self-start" text={t`For being online`}>
                  <div className="backgroundColorLightYellow h-3 w-3 rounded-[3px]" />
                  <Text className="whitespace-nowrap" type="body1-semibold">
                    <Trans>Hive Points</Trans>: {formatNumber(total3, 0)}
                  </Text>
                </TextTooltip>
              </li>
              <li className="borderColorGray100to70 max-mobile:ml-0 max-mobile:border-0 max-mobile:pr-0 mr-4 -ml-[1px] border-l-[1px] border-solid pl-4">
                <TextTooltip className="flex items-center gap-2 self-start" text={t`For referral program`}>
                  <div className="h-3 w-3 rounded-[3px] bg-[#35FFE4]" />
                  <Text className="whitespace-nowrap" type="body1-semibold">
                    <Trans>Referral Points</Trans>: {formatNumber(total4, 0)}
                  </Text>
                </TextTooltip>
              </li>
            </ul>
          </div>
        ) : (
          <div className="flex">
            <Text
              className="mr-4 flex pr-4"
              type="subtitle-semibold"
              style={{ borderRight: `1px solid ${theme.colorGray100to70}` }}
            >
              {title.plural}
            </Text>
            <Text className="flex gap-2" type="subtitle-semibold">
              {isPercents
                ? (() => {
                    const value = data?.[period] ? formatNumber(total, 2) : ''
                    return <Trans>Average: {value}%</Trans>
                  })()
                : (() => {
                    const value = data?.[period] ? formatNumber(total + total2, 0) : ''
                    return <Trans>Total: {value}</Trans>
                  })()}
              {!data?.[period] && <Spinner size="small" />}
            </Text>
          </div>
        )}
        <PeriodTabs
          className="max-mobile:-my-0 max-mobile:mt-3 -my-[2px] shrink-0"
          value={period}
          onChange={onPeriodChange}
        />
      </div>
      {data?.[period] ? (
        <div className="-mx-4 -mb-3">
          <OverlayScrollbarsComponent
            options={{
              overflow: {
                x: isMobile ? 'scroll' : 'hidden',
                y: 'hidden'
              },
              scrollbars: {
                autoHide: 'leave',
                autoHideDelay: 500,
                dragScroll: true,
                theme: isThemeDark ? 'os-theme-light' : 'os-theme-dark'
              }
            }}
            defer
          >
            <div className="relative px-4 pb-3" style={{ maxWidth: 'calc(100vw - 49px)' }}>
              {/*
                An emptiness test, NOT `total <= 0`: a window whose series sum to zero
                or below can still have bars to show — a user whose only points are
                Calls points, or one whose net window is negative — and covering those
                bars with "No data available yet" is wrong. Deliberately not
                `items.length === 0` either: nn-backend fills every bucket
                (`fillValues(period, entryStats, '0')`), so the series is dense and a
                length test would never fire.
              */}
              {items.every((item) => !item.value && !item.value2 && !item.value3 && !item.value4 && !item.value5) && (
                <div className="absolute inset-0 -mt-6 flex items-center justify-center">
                  <div className="backgroundColorGray100to70 z-[1] ml-4 rounded-xl px-8 py-6">
                    <Text className="max-mobile:block max-mobile:text-center" type="body1-regular">
                      {noDataText || <Trans>No data available yet</Trans>}
                    </Text>
                  </div>
                </div>
              )}
              <div className="max-mobile:min-w-[700px] mt-5 min-w-[520px]">
                <Chart
                  data={items}
                  formatXAxisTitle={(value) => formatChartXAxisTitle(value, period)}
                  formatTooltipTitle={(value) =>
                    `${formatNumber(value, isPercents ? 2 : 0)}${isPercents ? '%' : ''} ${Number(value) === 1 ? title.singular : title.plural}`
                  }
                  formatTooltipDescription={(value) => formatChartTooltipDescription(value)}
                  referenceLineValue={targetDate ? getReferenceLineValue(items, period, targetDate) : undefined}
                  isPercents={isPercents}
                  tooltipNode={tooltipNode}
                />
              </div>
            </div>
          </OverlayScrollbarsComponent>
        </div>
      ) : (
        <div className="flex h-[275px] items-center justify-center">
          <Spinner />
        </div>
      )}
    </Block>
  )
}

function getReferenceLineValue(
  items: { title: string; value: number }[],
  period: Period,
  { date }: { title: string; date: string }
): string | undefined {
  return items.reduce(
    (result, { title }) => {
      if (result) {
        return result
      }

      if (period === 'day' && dayjs(title).isSame(date, 'hour')) {
        result = title
      }

      if (period === 'month' && dayjs(title).isSame(date, 'day')) {
        result = title
      }

      if (period === 'year' && dayjs(title).isSame(date, 'month')) {
        result = title
      }

      return result
    },
    undefined as string | undefined
  )
}
